import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TrendUp, Fire, Recycle, ShieldWarning } from '@phosphor-icons/react';
import { devApi } from './devApi';

const yen = (v) => `¥${Math.round(v || 0).toLocaleString('ja-JP')}`;

const Card = ({ title, hint, icon: Icon, children, testid }) => (
  <section className="rounded-lg border border-zinc-300 bg-white p-4 shadow-sm" data-testid={testid}>
    <p className="text-sm font-bold text-zinc-900">
      {Icon && <Icon size={14} className="mr-1 inline text-teal-700" />}{title}
    </p>
    {hint && <p className="text-xs text-zinc-600">{hint}</p>}
    <div className="mt-3">{children}</div>
  </section>
);

const heatTone = (n) => {
  if (!n) return 'bg-zinc-100 text-zinc-400';
  if (n === 1) return 'bg-amber-200 text-amber-900';
  if (n === 2) return 'bg-amber-400 text-amber-950';
  return 'bg-rose-500 text-white';
};

/** 傾向の拡張と原価インテリジェンス — 何が繰り返し起きているか、どこにお金が消えているか。 */
export const Trends = ({ testid = 'dev-trends' }) => {
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const barRef = useRef(null);
  const heatRef = useRef(null);

  useEffect(() => { devApi.trends(12).then(setD).catch(() => {}); }, []);
  useEffect(() => {
    [barRef, heatRef].forEach((r) => {
      if (r.current) r.current.scrollLeft = r.current.scrollWidth;
    });
  }, [d]);
  if (!d) return null;

  const maxFail = Math.max(...d.timeline.map((r) => r.failures + r.revisions), 1);
  const c = d.cost;

  return (
    <div className="space-y-3" data-testid={testid}>
      <Card title="不具合と改訂の推移（直近12か月）" hint="山になっている月は、その時期の設計・調達を振り返る価値があります"
        icon={TrendUp} testid={`${testid}-timeline`}>
        <div ref={barRef} className="flex items-end gap-1 overflow-x-auto">
          {d.timeline.map((r) => (
            <div key={r.month} className="flex min-w-[46px] flex-1 flex-col items-center gap-1"
              data-testid={`${testid}-month-${r.month}`}>
              <div className="flex h-28 w-full flex-col justify-end gap-0.5">
                <div className="w-full rounded-t bg-rose-500"
                  style={{ height: `${(r.failures / maxFail) * 100}%` }} title={`不具合 ${r.failures}件`} />
                <div className="w-full bg-amber-400"
                  style={{ height: `${(r.revisions / maxFail) * 100}%` }} title={`改訂 ${r.revisions}回`} />
              </div>
              <p className="text-[10px] tabular-nums text-zinc-600">{r.month.slice(5)}月</p>
              <p className="text-[10px] font-bold tabular-nums text-zinc-900">
                {r.failures}/{r.revisions}
              </p>
            </div>
          ))}
        </div>
        <p className="mt-2 text-xs text-zinc-600">
          <span className="mr-2"><span className="mr-1 inline-block h-2 w-2 rounded bg-rose-500" />不具合</span>
          <span><span className="mr-1 inline-block h-2 w-2 rounded bg-amber-400" />改訂</span>
        </p>
      </Card>

      <div className="grid gap-3 lg:grid-cols-2">
        <Card title="スクラップの原因" hint="廃棄につながっている理由" icon={Recycle}
          testid={`${testid}-scrap`}>
          <ul className="space-y-1">
            {d.scrap_causes.map((r) => (
              <li key={r.label} className="flex items-baseline justify-between gap-2 text-sm"
                data-testid={`${testid}-scrap-${r.label}`}>
                <span className="min-w-0 truncate">{r.label}</span>
                <span className="shrink-0 font-bold tabular-nums">{r.count}件 / {r.qty}</span>
              </li>
            ))}
            {d.scrap_causes.length === 0 && <p className="text-sm text-zinc-500">記録はありません。</p>}
          </ul>
        </Card>

        <Card title="手戻りの原因" hint="改訂・手直しにつながっている分類" icon={Fire}
          testid={`${testid}-rework`}>
          <ul className="space-y-1">
            {d.rework_causes.map((r) => (
              <li key={`${r.kind}-${r.label}`} className="flex items-baseline justify-between gap-2 text-sm"
                data-testid={`${testid}-rework-${r.label}`}>
                <span className="min-w-0 truncate">{r.label}
                  <span className="ml-1 text-xs text-zinc-500">{r.kind_label}</span>
                </span>
                <span className="shrink-0 font-bold tabular-nums">{r.count}件</span>
              </li>
            ))}
            {d.rework_causes.length === 0 && <p className="text-sm text-zinc-500">記録はありません。</p>}
          </ul>
        </Card>
      </div>

      <Card title="図面の改訂ヒートマップ" hint="濃い月に改訂が集中しています。繰り返す図面は元から見直す価値があります"
        testid={`${testid}-heatmap`}>
        {d.revision_heatmap.rows.length === 0 ? (
          <p className="text-sm text-zinc-500">改訂の記録はまだありません。</p>
        ) : (
          <div ref={heatRef} className="overflow-x-auto">
            <table className="text-xs">
              <thead>
                <tr>
                  <th className="sticky left-0 bg-white py-1 pr-2 text-left font-semibold text-zinc-600">図面</th>
                  {d.revision_heatmap.months.map((m) => (
                    <th key={m.month} className="px-1 py-1 font-normal text-zinc-500">{m.month.slice(5)}</th>
                  ))}
                  <th className="pl-2 font-semibold text-zinc-600">計</th>
                </tr>
              </thead>
              <tbody>
                {d.revision_heatmap.rows.map((r) => (
                  <tr key={r.drawing_no} data-testid={`${testid}-heat-${r.drawing_no}`}>
                    <td className="sticky left-0 max-w-[180px] truncate bg-white py-1 pr-2 text-sm text-zinc-900">
                      {r.drawing_no}
                      <span className="ml-1 text-xs text-zinc-500">{r.part_name}</span>
                    </td>
                    {r.cells.map((cell) => (
                      <td key={cell.month} className="px-0.5 py-0.5">
                        <span className={`flex h-6 w-7 items-center justify-center rounded ${heatTone(cell.count)}`}>
                          {cell.count || ''}
                        </span>
                      </td>
                    ))}
                    <td className="pl-2 text-sm font-bold tabular-nums">{r.total}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <div className="grid gap-3 lg:grid-cols-2">
        <Card title="部品の信頼性" hint="投入回数に対して不具合が出た割合（低い順）" icon={ShieldWarning}
          testid={`${testid}-reliability`}>
          <ul className="space-y-1.5">
            {d.component_reliability.map((r) => (
              <li key={r.part_name} data-testid={`${testid}-reliability-${r.part_name}`}>
                <div className="flex items-baseline justify-between gap-2 text-sm">
                  <span className="min-w-0 truncate text-zinc-900">{r.part_name}</span>
                  <span className={`shrink-0 font-bold tabular-nums ${r.reliability < 70 ? 'text-rose-700' : 'text-emerald-700'}`}>
                    {r.reliability}%
                  </span>
                </div>
                <p className="text-xs text-zinc-600">
                  投入 {r.installs}回 / 不具合 {r.failures}件
                  {r.scrapped_qty > 0 ? ` / 廃棄 ${r.scrapped_qty}` : ''}
                  {r.projects?.length ? ` · ${r.projects.join('、')}` : ''}
                </p>
              </li>
            ))}
            {d.component_reliability.length === 0 && <p className="text-sm text-zinc-500">記録はありません。</p>}
          </ul>
        </Card>

        <Card title="原価インテリジェンス"
          hint={`想定コストが入っている案件 ${c.with_estimate}件 · 累計 ${yen(c.total_final)}（スクラップ ${yen(c.total_scrap)}）`}
          testid={`${testid}-cost`}>
          <p className="text-xs font-semibold text-zinc-700">想定 vs 実績（超過が大きい順）</p>
          <ul className="mt-1 space-y-1">
            {c.overruns.map((r) => (
              <li key={r.development_id} data-testid={`${testid}-overrun-${r.development_id}`}>
                <button type="button" className="w-full text-left text-sm hover:underline"
                  onClick={() => nav(`/engineering/developments/${r.development_id}`)}>
                  <span className="font-semibold text-zinc-900">{r.label}</span>
                  <span className="ml-2 text-xs text-zinc-600">
                    想定 {yen(r.estimated_cost)} → 実績 {yen(r.final_cost)}
                  </span>
                  <span className="ml-2 font-bold text-rose-700">+{yen(r.variance)}</span>
                </button>
              </li>
            ))}
            {c.overruns.length === 0 && (
              <p className="text-sm text-zinc-500">
                想定コストと比較できる案件がまだありません（構想の「想定コスト」に金額を入れてください）。
              </p>
            )}
          </ul>
          <p className="mt-3 text-xs font-semibold text-zinc-700">高額なスクラップ</p>
          <ul className="mt-1 space-y-1">
            {c.expensive_scrap.map((r) => (
              <li key={r.development_id} data-testid={`${testid}-scrapcost-${r.development_id}`}>
                <button type="button" className="w-full text-left text-sm hover:underline"
                  onClick={() => nav(`/engineering/developments/${r.development_id}`)}>
                  <span className="text-zinc-900">{r.label}</span>
                  <span className="ml-2 font-bold tabular-nums text-amber-800">{yen(r.scrap_cost)}</span>
                </button>
              </li>
            ))}
            {c.expensive_scrap.length === 0 && <p className="text-sm text-zinc-500">スクラップ費はありません。</p>}
          </ul>
        </Card>
      </div>
    </div>
  );
};
